import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    approveVisit,
    rejectVisit,
    verifyVisitOtp,
    checkInVisit,
    checkOutVisit,
    deleteVisit,
} from '../../../../redux/actions/SocietyPortal/VmsAction/VmsAction';

// The visit transitions, plus the dialog bookkeeping they need. Both the list
// and the detail screen drive the same set, so the wiring lives here once and
// each screen only renders the dialogs.
//
// `pending` holds the visit a dialog is currently asking about — one slot,
// because a visit is only ever in one state and so can only offer one
// text-collecting transition at a time.
export const useVisitTransitions = (onDone) => {
    const dispatch = useDispatch();
    const acting = useSelector(state => state.VmsReducer?.visits?.acting);

    const [pending, setPending] = useState(null); // { kind, visit }

    const close = useCallback(() => setPending(null), []);

    const finish = useCallback(() => {
        setPending(null);
        onDone?.();
    }, [onDone]);

    // Approve takes no body, so it fires straight away rather than opening a
    // dialog with nothing in it. Every other transition either collects text or
    // needs a warning first.
    const approve = useCallback((visit) => {
        dispatch(approveVisit({ id: visit.visit_id, onSuccess: finish }));
    }, [dispatch, finish]);

    const submitReject = useCallback((reason) => {
        dispatch(rejectVisit({ id: pending.visit.visit_id, reason: reason || undefined, onSuccess: finish }));
    }, [dispatch, pending, finish]);

    const submitOtp = useCallback((otp) => {
        dispatch(verifyVisitOtp({ id: pending.visit.visit_id, otp, onSuccess: finish }));
    }, [dispatch, pending, finish]);

    const submitCheckIn = useCallback((entryGate) => {
        dispatch(checkInVisit({ id: pending.visit.visit_id, entry_gate: entryGate || undefined, onSuccess: finish }));
    }, [dispatch, pending, finish]);

    const submitCheckOut = useCallback((exitGate) => {
        dispatch(checkOutVisit({ id: pending.visit.visit_id, exit_gate: exitGate || undefined, onSuccess: finish }));
    }, [dispatch, pending, finish]);

    const submitCancel = useCallback((afterCancel) => {
        dispatch(deleteVisit({
            id: pending.visit.visit_id,
            onSuccess: () => { setPending(null); (afterCancel || onDone)?.(); },
        }));
    }, [dispatch, pending, onDone]);

    // Maps a visitActions key onto either an immediate dispatch or a dialog.
    const run = useCallback((key, visit) => {
        if (key === 'approve') return approve(visit);
        setPending({ kind: key, visit });
    }, [approve]);

    return {
        acting: !!acting,
        pending,
        kind: pending?.kind || null,
        visit: pending?.visit || null,
        open: (kind, visit) => setPending({ kind, visit }),
        run,
        close,
        submitReject,
        submitOtp,
        submitCheckIn,
        submitCheckOut,
        submitCancel,
    };
};

export default useVisitTransitions;
